import {
  Box,
  CircularProgress,
  IconButton,
  Pagination,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CommonHeader from "../../CommonComponents/CommonHeader";
import ButtonCommon from "../../CommonComponents/ButtonCommon";
import { Add } from "@mui/icons-material";
import { Eye } from "../../assets/svg";
import { ReactComponent as Trash } from "../../assets/svg/trash.svg";
import { ReactComponent as Approval } from '../../assets/svg/approval.svg';
import { useEffect, useState } from "react";
import { showErrorToast, showSuccessToast } from "../../toastUtils";
import { deleteFrameworks, getAllFrameworks } from "../../api/framework";
import ComponentWrapper from "../../CommonComponents/ComponentWrapper";
import SearchInput from "../../CommonComponents/SearchInput";
import SelectCommon from "../../CommonComponents/SelectCommon";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../components/UserContext";
import { getApprovalById } from "../../api/approval";
import ChangeRequestViewModal from "../../CommonComponents/ChangeRequestViewModal";
import CommonConfirmationModal from "../../CommonComponents/CommonConfirmationModal";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F9FAFB",
    color: "#000000",
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const FrameworkPage = () => {
  const { currentUser } = useUser();

  const [loading, setLoading] = useState(false);
  const [frameworks, setFrameworks] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [statusValue, setStatusValue] = useState('all');
  const [filterData, setFilteredData] = useState([])
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [viewFramework, setViewFramework] = useState(null)
  // const [currentFramework, setCurrentFramework] = useState(null)
  const [approvalLoader, setApprovalLoader] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [frameworkToDelete, setFrameworkToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate()

  const fetchFrameworks = async () => {
    setLoading(true);
    try {
      const res = await getAllFrameworks(page, perPage);
      if (res.status_code === 200) {
        setLoading(false);
        setFrameworks(res.data.frameworks);
        setFilteredData(res?.data?.frameworks);
        setTotalCount(res.data?.total_frameworks || 0);
      } else {
        setLoading(false);
        showErrorToast(res.message);
      }
    } catch (err) {
      setLoading(false);
      showErrorToast(err);
      setFrameworks([]);
    }
  };

  useEffect(() => {
    fetchFrameworks();
    //eslint-disable-next-line
  }, [page, perPage]);

  const handleEditCategory = (framework) => {
    // setCurrentFramework(framework);
    navigate(`/master/frameworks/view/${framework.framework_id}`)
  };

  // Add view handler
  const handleViewClick = async (framework) => {
    setApprovalLoader(framework.approval_id);
    try {
      const response = await getApprovalById(framework.approval_id);

      if (response.success) {
        const approvalData = response.data;

        setViewFramework({
          ...approvalData,
          // requested_by: approvalData.requested_by || "Unknown",
          // requested_at: approvalData.requested_at || new Date().toISOString(),
        });

        setViewModalOpen(true);
      } else {
        console.error('No data received for framework approval.');
      }
    } catch (error) {
      console.error('Error fetching framework approval details:', error);
    } finally {
      setApprovalLoader(null);
    }
  };

  const handleDeleteClick = (framework) => {
    setFrameworkToDelete(framework);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!frameworkToDelete?.framework_id) {
      showErrorToast('Invalid framework selected for deletion.');
      return;
    }

    try {
      setDeleteLoading(true);

      const response = await deleteFrameworks(frameworkToDelete.framework_id);

      if (response.success) {
        showSuccessToast(response.message);
        fetchFrameworks();
      } else {
        showErrorToast(response.message || 'Failed to delete framework');
      }
    } catch (error) {
      console.error('Error deleting framework:', error); // 🔥
      showErrorToast(error.message);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setFrameworkToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFrameworkToDelete(null);
  };

  useEffect(() => {
    const data = frameworks ?? [];

    const filtered = data.filter((item) => {
      const search = searchValue?.toLowerCase() || '';
      const framework = item?.framework_name?.toLowerCase() || '';
      const isNameMatch = framework.includes(search);

      const isStatusMatch =
        statusValue?.toLowerCase() === 'all' ||
        item?.status?.toLowerCase() === statusValue?.toLowerCase();

      return isNameMatch && isStatusMatch;
    });

    setFilteredData(filtered);
  }, [searchValue, statusValue, frameworks]);

  return (
    <ComponentWrapper>
      {/* <Stack sx={{ maxWidth: "1000px" }}> */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          // alignItems: "center",
          flexDirection: 'column',
          mb: 2,
          gap: 1,
        }}
      >
        <CommonHeader
          title="Master Frameworks"
          subTitle="You can review, audit and change the status of master framework on this page."
        />
        {/* <ButtonCommon
          icon={<Add />}
          //   onClick={() => setOpen(true)}
          sx={{ whiteSpace: "nowrap" }}
        >
          Add Frame
        </ButtonCommon> */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'end', flexDirection: 'row', mb: 2 }}>
          <SearchInput
            placeholder="Search by Name"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <SelectCommon
            label="Status"
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value)}
            options={[
              { label: "All", value: "all" },
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
              { label: "Draft", value: "draft" },
            ]}
          />
          <ButtonCommon icon={<Add />}
            onClick={() => navigate('/master/frameworks/create')}
          >
            Add Framework
          </ButtonCommon>
        </Box>
      </Box>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: "12px" }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Framework Name</StyledTableCell>
              <StyledTableCell>Description</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filterData && filterData.length > 0 ? (
              filterData.map((row) => {
                // const initials = row.full_name
                //   ? row.full_name
                //     .split(' ')
                //     .map((n) => n[0])
                //     .join('')
                //     .toUpperCase()
                //   : '?';
                const initials = row?.framework_name?.charAt(0).toUpperCase();
                return (
                  <TableRow key={row.framework_id} hover>
                    <StyledTableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            backgroundColor: "#FFE5DD",
                            color: "#FF6348",
                            width: "30px",
                            height: "30px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            fontSize: "14px",
                          }}
                        >
                          {initials}
                        </Box>
                        <Typography sx={{ fontSize: "14px" }}>
                          {row.framework_name}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontSize: "14px" }}>
                      {row.framework_description}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        textTransform: "capitalize",
                        color:
                          row.status === "active"
                            ? "#00B894"
                            : row.status === "inactive"
                              ? "#FF946D"
                              : "#6994FF",
                        fontWeight: 600,
                      }}
                    >
                      {row.status}
                    </StyledTableCell>
                    <StyledTableCell sx={{ px: 0, width: '140px' }}>
                      {currentUser?.zoneinfo === "super_admin" && <Tooltip title="Approval" arrow>
                        <IconButton
                          size="small"
                          disabled={
                            row.status !== "draft" ||
                            currentUser?.zoneinfo === "product_user"
                          }
                          sx={{
                            color: (theme) =>
                              row.status !== "draft" ||
                                currentUser?.zoneinfo === "product_user"
                                ? theme.palette.grey[400]
                                : "inherit",
                          }}
                          onClick={() => handleViewClick(row)}
                        >
                          {/* {approvalLoader ? <CircularProgress size={20} /> : <Eye />} */}
                          {(row.approval_id !== null && approvalLoader === row.approval_id) ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Approval />
                          )}
                        </IconButton>
                      </Tooltip>}

                      <Tooltip title="View" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleEditCategory(row)}
                          disabled={row.status === "draft" || row.approval_id}
                          sx={{
                            color: (theme) =>
                              row.status === "draft"
                                ? theme.palette.grey[400]
                                : "inherit",
                          }}
                        >
                          <Eye />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(row)}
                          disabled={row.status === "draft" || row.status === "inactive" || row.approval_id}
                          sx={{
                            color: (theme) =>
                              row.status === "draft"
                                ? theme.palette.grey[400]
                                : "inherit",
                          }}
                        >
                          <Trash />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <StyledTableCell colSpan={4}>
                  <Typography
                    sx={{
                      textAlign: "center",
                      py: 2,
                      color: "text.secondary",
                      fontSize: "14px",
                    }}
                  >
                    No data available
                  </Typography>
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={Math.ceil(totalCount / perPage)}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          shape="rounded"
          variant="outlined"
        />
      </Box>

      <CommonConfirmationModal
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        description={`Are you sure you want to delete the framework?`}
        loading={deleteLoading}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      <ChangeRequestViewModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        fetchData={fetchFrameworks}
        title={`Framework ${viewFramework?.action_type} request`}
        approvalId={viewFramework?.approval_id}
        beforeData={viewFramework?.data_before ? {
          name: viewFramework?.data_before?.framework_name,
          description: viewFramework?.data_before?.framework_description,
          supportedLanguages: viewFramework?.data_before?.supported_languages,
          countryList: viewFramework?.data_before?.country_list,
          stateList: viewFramework?.data_before?.state_list,
          frameworkProperties: viewFramework?.data_before?.framework_properties,
          status: viewFramework?.data_before?.is_deleted ? 'Inactive' : 'Active'
        } : null}
        afterData={{
          name: viewFramework?.data_after?.framework_name,
          description: viewFramework?.data_after?.framework_description,
          supportedLanguages: viewFramework?.data_after?.supported_languages,
          countryList: viewFramework?.data_after?.country_list,
          stateList: viewFramework?.data_after?.state_list,
          frameworkProperties: viewFramework?.data_after?.framework_properties,
          status: viewFramework?.data_after?.is_deleted ? 'Inactive' : 'Active'
        }}
        requestedBy={viewFramework?.requested_by}
        requestedAt={viewFramework?.requested_at}
        actionType={viewFramework?.action_type}
      // onApprove={() => handleApproveLanguage(viewFramework)}
      // onReject={() => handleRejectLanguage(viewFramework)}
      />
      {/* </Stack> */}
    </ComponentWrapper>
  );
};
export default FrameworkPage;
